<template>
    <!-- The overlay is a sibling of the scrolling list, so it stays in view when the list is scrolled. -->
    <div class="selection-list-wrapper" :style="{ height }">
        <div class="selection-list selection-list-container" :style="{ height, maxHeight: height }" tabindex="0">
            <div
                v-for="(option, index) in filteredOptions"
                :key="index"
                @click="selectOption(option)"
                class="option-wrapper"
                :class="{ 'is-selected': isSelected(option), 'is-readonly': readonly, 'is-disabled': isOptionDisabled?.(option) }"
            >
                <div>
                    <span class="icon" v-if="multiSelect && !readonly">
                        <i class="fas fa-check-square" v-if="isSelected(option)"></i>
                        <i class="far fa-square" v-else></i>
                    </span>
                </div>

                <div class="is-flex-grow-1">
                    <slot name="option" :option="option" :index="index"></slot>
                </div>
            </div>
        </div>
        <div class="loading-overlay" v-if="isLoading">
            <InlineSpinner />
        </div>
    </div>
</template>

<script setup lang="ts" generic="T extends NonNullable<unknown>">
    import InlineSpinner from "./InlineSpinner.vue";
    import { computed } from "vue";
    import objectMatcher from "../Common/objectMatcher";

    const props = defineProps<{
        options: T[];
        isLoading?: boolean;
        multiSelect?: boolean;
        readonly?: boolean; //Disables selecting options, for using this component as a read-only scrollable list.
        isOptionDisabled?: (option: T) => boolean; //Disables selecting individual options, keeping the rest of the list interactive.
        filterQuery?: string;
        filterFunction?: (option: T) => string;
        comparingFunction?: (a: T, b: T) => boolean; //Returns true if equal, this is used for making sure that right elements are removed from modelValue when clicking checkboxes.
        height?: string; //Overrides the default 300px height/max-height, e.g. "100%" to fill the parent container.
    }>();

    const comparingFunction = props.comparingFunction || ((a: T, b: T) => a === b);

    const model = defineModel<T[] | T | null | undefined>();
    const emit = defineEmits<{
        (e: "change", option: T | null): void;
    }>();

    function selectOption(option: T) {
        if (props.readonly || props.isOptionDisabled?.(option)) {
            return;
        }

        if (props.multiSelect && Array.isArray(model.value)) {
            if (model.value.some(selected => comparingFunction(selected, option))) {
                // Remove option
                model.value = model.value.filter(selectedOption => !comparingFunction(selectedOption, option));
            } else {
                // Add option
                model.value = [...model.value, option];
            }
        } else {
            model.value = option;
        }
        emit("change", option);
    }

    function isSelected(option: T) {
        if (props.readonly) {
            return false;
        }

        if (props.multiSelect && Array.isArray(model.value)) {
            return model.value.some(selected => comparingFunction(selected, option));
        } else {
            return (
                model.value !== null && model.value !== undefined && !Array.isArray(model.value) && comparingFunction(model.value, option)
            );
        }
    }

    const filteredOptions = computed((): T[] => {
        if (props.filterFunction) {
            return props.options.filter(option => {
                return objectMatcher.matchString(props.filterQuery, props.filterFunction!(option));
            });
        }

        return props.options;
    });

    function selectAllVisible() {
        if (props.multiSelect) {
            //It's about incrementally adding to the model only new values.
            const currentValues = (model.value as T[]) || [];
            const newValues = filteredOptions.value.filter(option => !currentValues.some(selected => comparingFunction(selected, option)));
            model.value = [...currentValues, ...newValues];
        }
        emit("change", null);
        //Otherwise ignore.
    }

    function deselectAllVisible() {
        if (props.multiSelect && Array.isArray(model.value)) {
            model.value = model.value.filter(option => !filteredOptions.value.some(filtered => comparingFunction(filtered, option)));
        } else {
            model.value = null;
        }
        emit("change", null);
    }

    defineExpose({
        selectAllVisible,
        deselectAllVisible,
    });
</script>
